import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  Autocomplete,
  MultipleAutocomplete,
} from '@src/common/components/Autocomplete';
import { Button } from '@src/common/components/button/Button';
import { Popover } from '@src/common/components/Popover';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type {
  ControllerRenderProps,
  DefaultValues,
  FieldValues,
  Path,
} from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Option = { label: string; value: string };
export type Filter<T extends FieldValues> = {
  key: Path<T>;
  options?: (string | Option)[];
  multiple?: boolean;
  renderFilter?: (field: ControllerRenderProps<T, Path<T>>) => ReactNode;
};

export const Filters = <T extends FieldValues>({
  onChange,
  filters,
}: {
  onChange: (value: T) => void;
  filters: Filter<T>[];
}) => {
  const { t } = useTranslation();
  const [appliedFilters, setAppliedFilters] = useState<Partial<T>>({});
  const emptyFormState = filters.reduce(
    (acc, filter) => ({ ...acc, [filter.key]: null }),
    {} as DefaultValues<T>,
  );
  const { handleSubmit, control, reset } = useForm<T>();
  const filtersCount = Object.values(appliedFilters).filter(Boolean).length;

  const onSubmit = (value: T) => {
    onChange(value);
    setAppliedFilters(value);
  };

  return (
    <Popover
      openPopoverComponent={t('filters', { count: filtersCount })}
      openPopoverProps={{
        variant: filtersCount > 0 ? 'outlined' : 'text',
        startIcon: <FilterIcon />,
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <form
        className={FiltersContainer}
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        {filters.map(({ options, key, multiple, renderFilter }) => (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field }) =>
              renderFilter ? (
                <>{renderFilter(field as ControllerRenderProps<T, Path<T>>)}</>
              ) : (
                renderAutocomplete(options ?? [], field, key, t, multiple)
              )
            }
          />
        ))}

        <Stack
          flexDirection="row"
          gap={theme.spacing['4']}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            type="submit"
            onClick={() => reset(emptyFormState)}
          >
            {t('reset')}
          </Button>
          <Button variant="contained" type="submit">
            {t('apply')}
          </Button>
        </Stack>
      </form>
    </Popover>
  );
};

const FiltersContainer = css((theme) => ({
  width: '368px',
  padding: theme.spacing['4'],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing['6'],
}));

const renderAutocomplete = <TFieldValues extends FieldValues>(
  options: (string | Option)[],
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
  key: string,
  t: TFunction,
  multiple?: boolean,
) => {
  const getOptionLabel = () => {
    return (option: unknown) =>
      typeof option === 'string' ? option : ((option as Option).label ?? '');
  };
  const getRenderInput = () => {
    return (params: AutocompleteRenderInputParams) => (
      <TextInput {...params} label={t(`fields.${key}`)} />
    );
  };
  if (multiple) {
    return (
      <MultipleAutocomplete
        {...field}
        onChange={(_, values) => {
          field.onChange(
            values
              .map((option) =>
                typeof option === 'string'
                  ? option
                  : (option as { value: string }).value,
              )
              .filter(Boolean),
          );
        }}
        options={options}
        value={
          Array.isArray(field.value)
            ? options.filter((option) =>
                field.value.includes(
                  typeof option === 'object' ? option.value : option,
                ),
              )
            : []
        }
        getOptionLabel={getOptionLabel()}
        renderInput={getRenderInput()}
      />
    );
  } else {
    return (
      <Autocomplete
        {...field}
        onChange={(_, value) => {
          if (value && typeof value === 'object') {
            return field.onChange((value as Option).value);
          }
          field.onChange(value);
        }}
        options={options}
        value={
          field.value
            ? options.filter((option) => {
                if (typeof option === 'object') {
                  return field.value === option.value;
                }
                return field.value === option;
              })[0]
            : null
        }
        getOptionLabel={getOptionLabel()}
        renderInput={getRenderInput()}
      />
    );
  }
};
